import React from "react";
var __jsx = React.createElement;
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { UnstyledButton } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpRightAndDownLeftFromCenter, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { useResolveReminder } from "@/hooks/tasks";
import { TaskDetail } from "@/components/task-detail";
import { TaskActionsProvider } from "@/components/task-actions-provider";
import { MantineThemeProvider } from "@/components/mantine-theme";
import { StatusChip } from "@/components/status-chip";
import { getTaskDisplayProps } from "@/components/patient-tasks";
import { useTagUndoStore } from "@/stores/tag-undo";
import { LegacyComposerHost } from "./LegacyComposerHost";
import Modal from "../common/Modal";
/** Renders the patient task detail inside the legacy Modal shell for the old patients page. */
export function PatientTaskModal(_ref) {
  var reminder = _ref.reminder,
    patientUid = _ref.patientUid,
    onClose = _ref.onClose,
    onResolved = _ref.onResolved,
    onChangeContext = _ref.onChangeContext,
    onSaved = _ref.onSaved,
    _ref$actions = _ref.actions,
    actions = _ref$actions === void 0 ? {} : _ref$actions,
    _ref$isMinimized = _ref.isMinimized,
    isMinimized = _ref$isMinimized === void 0 ? false : _ref$isMinimized,
    onMinimize = _ref.onMinimize,
    onRestore = _ref.onRestore;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var resetTagUndo = useTagUndoStore(function (s) {
    return s.reset;
  });
  var _useResolveReminder = useResolveReminder(patientUid),
    resolveReminder = _useResolveReminder.mutate,
    isMarkingDone = _useResolveReminder.isLoading;
  if (!reminder) return null;
  var handleClose = function handleClose() {
    resetTagUndo();
    onClose();
  };
  var handleMarkDone = function handleMarkDone() {
    resolveReminder(reminder.id, {
      onSuccess: function onSuccess() {
        onResolved === null || onResolved === void 0 || onResolved();
        handleClose();
      }
    });
  };
  if (isMinimized) {
    var _getTaskDisplayProps = getTaskDisplayProps(reminder),
      intent = _getTaskDisplayProps.intent,
      label = _getTaskDisplayProps.label;
    return /*#__PURE__*/createPortal(/*#__PURE__*/React.createElement(MantineThemeProvider, null, /*#__PURE__*/React.createElement(LegacyComposerHost, null), /*#__PURE__*/React.createElement("div", {
      className: "pointer-events-auto fixed bottom-6 left-6 z-sds-1200 flex max-w-sm items-center justify-between gap-3 rounded-sds-300 border border-sds-brand-default bg-sds-base-default px-4 py-3 shadow-sds-400"
    }, /*#__PURE__*/React.createElement(UnstyledButton, {
      "data-testid": "patient-task-modal-restore-card",
      "aria-label": t("expand", {
        defaultValue: "Expand"
      }),
      onClick: onRestore,
      className: "flex min-w-0 flex-1 items-center gap-3 text-left"
    }, /*#__PURE__*/React.createElement(StatusChip, {
      intent: intent,
      label: label
    }), /*#__PURE__*/React.createElement("p", {
      className: "truncate text-style-body-strong text-sds-base-default"
    }, reminder.title || reminder.context)), /*#__PURE__*/React.createElement("div", {
      className: "flex flex-shrink-0 items-center gap-1"
    }, /*#__PURE__*/React.createElement(UnstyledButton, {
      "data-testid": "patient-task-modal-restore",
      "aria-label": t("expand", {
        defaultValue: "Expand"
      }),
      title: t("expand", {
        defaultValue: "Expand"
      }),
      onClick: onRestore,
      className: "flex h-8 w-8 items-center justify-center rounded-sds-200 text-sds-base-secondary transition-colors hover:bg-sds-base-secondary hover:text-sds-brand-default"
    }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
      icon: faUpRightAndDownLeftFromCenter,
      className: "h-4 w-4"
    })), /*#__PURE__*/React.createElement(UnstyledButton, {
      "data-testid": "patient-task-modal-close",
      "aria-label": t("close"),
      title: t("close"),
      onClick: handleClose,
      className: "flex h-8 w-8 items-center justify-center rounded-sds-200 text-sds-base-secondary transition-colors hover:bg-sds-base-secondary hover:text-sds-urgent-default"
    }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
      icon: faXmark,
      className: "h-4 w-4"
    }))))), document.body);
  }
  return /*#__PURE__*/React.createElement(MantineThemeProvider, null, /*#__PURE__*/React.createElement(LegacyComposerHost, null), /*#__PURE__*/React.createElement(Modal, {
    show: true,
    className: "mx-4 h-[85vh] max-h-[900px] w-full max-w-5xl overflow-hidden sm:mx-0 text-left"
  }, /*#__PURE__*/React.createElement(TaskActionsProvider, {
    actions: actions
  }, /*#__PURE__*/React.createElement(TaskDetail, {
    embedded: true,
    key: reminder.id,
    reminder: reminder,
    onClose: handleClose,
    onMinimize: onMinimize,
    onMarkDone: handleMarkDone,
    isMarkingDone: isMarkingDone,
    onChangeContext: onChangeContext,
    onSaved: onSaved
  }))));
}